/**
 * Product Hero Module JavaScript
 * Handles dynamic pack selection, pricing calculations, and cart functionality
 */

class ProductHero {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.productForm = document.querySelector(`#product-form-${sectionId}`);
    this.initialSubscriptionSavingsPercentage = '15%'; // Default fallback
    
    this.elements = this.initializeElements();
    this.mainEventController = null;
    this.quantityButtonCleanup = null;
    
    if (this.productForm) {
      this.init();
    }
  }

  initializeElements() {
    const productForm = this.productForm;
    if (!productForm) return {};


    return {
      subscriptionInfo: productForm.querySelector('.option__subscription-info'),
      // Alternative subscription info selector
      subscriptionInfoAlt: productForm.querySelector('[class*="subscription-info"]'),
      subscriptionBenefits: productForm.querySelector('.product__subscription-benefits'),
      productVariantsSelectBtn: productForm.querySelector('.product__variants-select-btn'),
      productVariantsSelectDropdown: productForm.querySelector('.product__variants-select-dropdown'),
      productVariantSelectOption: productForm.querySelectorAll('.product__variants-select-dropdown-item'),
      productVariantsOptions: productForm.querySelectorAll('.product__variants .option'),
      onetimePrice: productForm.querySelector('.single-option__price--onetime div'),
      onetimeOptionPrice: productForm.querySelector('.option__price--onetime div'),
      subscriptionOptionPrice: productForm.querySelector('.option__price--subscription div'),
      subscriptionOptionPriceDel: productForm.querySelector('.option__price--subscription del'),
      sellingPlan: productForm.querySelector(`#product-form-${this.sectionId}-sellingPlan`),
      // Alternative selectors for selling plan elements
      sellingPlanAlt: productForm.querySelector(`[id*="sellingPlan"]`),
      sellingPlanOptionsAlt: productForm.querySelector(`[id*="sellingPlanOptions"]`),
      productId: productForm.querySelector('[name="id"]'),
      sellingPlanOptions: productForm.querySelector(`#product-form-${this.sectionId}-sellingPlanOptions`),
      carouselSlides: document.querySelectorAll('#pdp-main-carousel .embla__slide'),
      purchaseSelectorLabel: productForm.querySelectorAll(`#product-form-${this.sectionId}-ProductSelect-option-1 .custom-radio-info label`),

      thumbnailSlides: [],
      mainCarousel: document.querySelector('#pdp-main-carousel'),
      variantsWrapper: productForm.querySelector('.product_variants-wrapper'),
      customVariants: productForm.querySelector('.product__custom-variants'),
      purchaseType: productForm.querySelectorAll(`input[name="product-form-${this.sectionId}-main-subscription"]`),
      customRadios: productForm.querySelectorAll('.custom-radio'),
      variantSelectorRadios: productForm.querySelectorAll('.variant-selector__radio'),
      productForm: productForm,
      quantityInput: productForm.querySelector('input[name="quantity"]'),
      submitButton: productForm.querySelector('.product__form-submit'),
      subLabelStrike: productForm.querySelector('label.purchaseType-subscription .label__strike-price'),
      subLabelFinal: productForm.querySelector('label.purchaseType-subscription .label__final-price'),
      otLabelStrike: productForm.querySelector('label.purchaseType-one-time .label__strike-price'),
      otLabelFinal: productForm.querySelector('label.purchaseType-one-time .label__final-price'),
      _customVariantItems: null,
      _packOptionButtons: null,
      _variantGridItems: null,

      // Getter methods for dynamic elements
      getCustomVariantItems: () => {
        if (!this.elements._customVariantItems) {
          this.elements._customVariantItems = this.productForm.querySelectorAll('.custom-variant-item');
        }
        return this.elements._customVariantItems;
      },

      getPackOptionButtons: () => {
        if (!this.elements._packOptionButtons) {
          this.elements._packOptionButtons = this.productForm.querySelectorAll('.pack-option-btn');
        }
        return this.elements._packOptionButtons || [];
      },

      getActivePackButton: () => {
        return this.productForm.querySelector('.pack-option-btn.active') || null;
      },

      getVariantGridItems: () => {
        if (!this.elements._variantGridItems) {
          this.elements._variantGridItems = document.querySelectorAll('.product__variants-grid-item');
        }
        return this.elements._variantGridItems;
      },

      refreshDynamicElements: () => {
        this.elements._customVariantItems = null;
        this.elements._packOptionButtons = null;
        this.elements._variantGridItems = null;
      }
    };
  }

  init() {
    // Clean up any existing listeners first
    this.cleanupMainEventListeners();

    // Create new abort controller for this initialization
    this.mainEventController = new AbortController();
    const eventOptions = { signal: this.mainEventController.signal };

    // Initialize pack configuration
    this.initializePackConfig();
    
    // Initialize savings percentage
    this.initializeSavingsPercentage();

    // Set up initial pack state
    this.setupInitialPackState();
    
    // Set up initial subscription state
    this.setupInitialSubscriptionState();
    
    // Set up event listeners
    this.setupEventListeners(eventOptions);
    
    // Set up quantity buttons
    this.setupQuantityButtons();
    
    // Set up form submission validation
    this.setupFormValidation();

    // Initialize carousel and variants
    this.initializeCarouselAndVariants();

    // Clean up on page unload
    window.addEventListener('beforeunload', () => {
      this.cleanup();
    }, { once: true });
  }

  initializePackConfig() {
    // Only initialize pack config if pack elements exist
    const cfgEl = document.getElementById(`pack-config-${this.sectionId}`);
    if (cfgEl) {
      try {
        window.packConfig = JSON.parse(cfgEl.textContent);
      } catch (err) {
        console.warn('Failed to parse pack config:', err);
        window.packConfig = {};
      }
    } else {
      // No pack config found - this is normal when pack functionality is disabled
      window.packConfig = {};
    }

    // Ensure window.packConfig is always an object
    if (!window.packConfig || typeof window.packConfig !== 'object') {
      window.packConfig = {};
    }
  }

  setupInitialPackState() {
    // Check if pack functionality is available
    const packButtons = this.productForm.querySelectorAll('.pack-option-btn');
    if (packButtons.length === 0) {
      // No pack functionality - this is normal for flavor selector mode
      return;
    }

    // Determine initial active pack button and size
    let initialActivePackButton = this.productForm.querySelector('.pack-option-btn.active');
    let initialPackSize = 1;

    if (initialActivePackButton) {
      initialPackSize = parseInt(initialActivePackButton.getAttribute('data-pack-size'));
    } else {
      // If no pack button is active, default to the 1-pack button and activate it visually
      initialActivePackButton = this.productForm.querySelector('.pack-option-btn[data-pack-size="1"]');
      if (initialActivePackButton) {
        initialActivePackButton.classList.add('active');
      }
    }

    // Call packClickHandler to set initial UI state based on the detected/default active pack
    if (initialActivePackButton) {
      this.packClickHandler(initialActivePackButton, initialPackSize.toString());
    } else {
      console.warn("Pack buttons found but couldn't determine initial active pack.");
      // Basic setup for pack products when pack buttons are present but misconfigured
      this.updateSubscriptionSavings(1);
      const initialSubscriptionRadio = this.productForm.querySelector(`input#product-form-${this.sectionId}-purchaseType--subscription`);
      const isInitialSubscriptionActive = initialSubscriptionRadio && initialSubscriptionRadio.checked;
      this.updatePackSaveTexts(isInitialSubscriptionActive);
    }
  }

  setupInitialSubscriptionState() {
    // Check if subscription radio is checked by default
    const subscriptionRadio = this.productForm.querySelector(`input#product-form-${this.sectionId}-purchaseType--subscription`);
    const isSubscriptionActive = subscriptionRadio && subscriptionRadio.checked;
    
    // Find selling plan input and set name attribute if subscription is active
    const sellingPlanInput = this.productForm.querySelector(`input[id*="sellingPlan"]`);
    if (sellingPlanInput) {
      if (isSubscriptionActive) {
        sellingPlanInput.setAttribute('name', 'selling_plan');
      } else {
        sellingPlanInput.removeAttribute('name');
      }
    }
    
    // Ensure subscription info visibility matches the radio state
    const subscriptionInfo = this.elements.subscriptionInfo || this.elements.subscriptionInfoAlt || this.productForm.querySelector('.option__subscription-info');
    if (subscriptionInfo) {
      if (isSubscriptionActive) {
        subscriptionInfo.classList.add('active');
      } else {
        subscriptionInfo.classList.remove('active');
      }
    }
  }

  // Helper to get selected variant data for 1-pack
  getSelectedVariantData() {
    const visualSelectorUsed = false; // This would be determined by section settings
    let selectedVariantEl;

    if (visualSelectorUsed) {
      selectedVariantEl = this.productForm.querySelector('.product__variants-grid-item.selected');
    } else {
      selectedVariantEl = this.productForm.querySelector('.product__variants-select-dropdown-item--selected');
    }

    if (selectedVariantEl) {
      return {
        id: selectedVariantEl.dataset.variantId || selectedVariantEl.dataset.id,
        price: selectedVariantEl.dataset.price,
        subPrice: selectedVariantEl.dataset.subPrice,
        priceRaw: selectedVariantEl.dataset.priceRaw,
        subPriceRaw: selectedVariantEl.dataset.subPriceRaw,
        serving: selectedVariantEl.dataset.serving,
        servingSubscription: selectedVariantEl.dataset.servingSubscription,
        element: selectedVariantEl
      };
    }
    return null;
  }

  // Helper to update selling plan selection
  updateSellingPlanSelection(packSize, sellingPlanOptionsEl, sellingPlanInputEl) {
    if (!sellingPlanOptionsEl || !sellingPlanInputEl) return;

    const packCfg = window.packConfig && window.packConfig[packSize];

    if (packCfg && packCfg.planId) {
      const optionExists = Array.from(sellingPlanOptionsEl.options).find(opt => opt.value === packCfg.planId);
      if (optionExists) {
        sellingPlanOptionsEl.value = packCfg.planId;
        sellingPlanInputEl.value = packCfg.planId;
        return;
      } else {
        console.warn(`Selling plan ID ${packCfg.planId} from packConfig not found for pack size ${packSize}. Falling back to default.`);
      }
    }

    let targetPlanValue;
    if (packSize === 2) {
      targetPlanValue = '1309933746';
    } else if (packSize === 3) {
      targetPlanValue = '1320517810';
    } else {
      const default30DayOption = Array.from(sellingPlanOptionsEl.options).find(option => option.textContent.includes('30 day'));
      if (default30DayOption) {
        targetPlanValue = default30DayOption.value;
      } else if (sellingPlanOptionsEl.options.length > 0) {
        targetPlanValue = sellingPlanOptionsEl.options[0].value;
      }
    }

    if (targetPlanValue) {
      const targetOption = Array.from(sellingPlanOptionsEl.options).find(opt => opt.value === targetPlanValue);
      if (targetOption) {
        sellingPlanOptionsEl.value = targetOption.value;
      } else if (sellingPlanOptionsEl.options.length > 0) {
        let fallbackOption;
        if (packSize === 1) fallbackOption = Array.from(sellingPlanOptionsEl.options).find(o => o.textContent.includes('30 day'));
        else if (packSize === 2) fallbackOption = Array.from(sellingPlanOptionsEl.options).find(o => o.textContent.includes('60 day'));
        else if (packSize === 3) fallbackOption = Array.from(sellingPlanOptionsEl.options).find(o => o.textContent.includes('90 day'));

        if (fallbackOption) sellingPlanOptionsEl.value = fallbackOption.value;
        else if (sellingPlanOptionsEl.options.length > 0) sellingPlanOptionsEl.selectedIndex = 0;
      }
    } else if (sellingPlanOptionsEl.options.length > 0) {
      sellingPlanOptionsEl.selectedIndex = 0;
    }
    sellingPlanInputEl.value = sellingPlanOptionsEl.value;
  }

  // Global function for pack click handlers
  packClickHandler(buttonElement, packSizeStr) {
    if (!this.productForm || !buttonElement) return;

    // Find the actual form where the pack button is located
    const actualForm = buttonElement.closest('form');
    if (!actualForm) {
      console.warn('Pack button not found within a form');
      return;
    }

    let previousPackSize = 1;
    const previouslyActiveButton = actualForm?.querySelector('.pack-option-btn.active') || this.elements.getActivePackButton();
    if (previouslyActiveButton) {
      previousPackSize = parseInt(previouslyActiveButton.getAttribute('data-pack-size'));
    }

    const packSize = parseInt(packSizeStr);

    const packButtons = actualForm?.querySelectorAll('.pack-option-btn') || this.elements.getPackOptionButtons();
    packButtons.forEach(btn => {
      btn.classList.remove('active');
    });
    buttonElement.classList.add('active');

    const variantsWrapper = actualForm?.querySelector('.product_variants-wrapper');
    const customVariants = actualForm?.querySelector('.product__custom-variants');

    // Conditional Quantity Reset
    if (packSize < previousPackSize) {
      actualForm?.querySelectorAll('.custom-variant-item input[type="number"]').forEach(input => {
        input.value = 0;
      });
    }

    // Extract section ID from the actual form
    const actualFormId = actualForm?.getAttribute('id') || '';
    const actualSectionId = actualFormId.replace('product-form-', '');
    
    const sellingPlanOptions = actualForm?.querySelector(`#product-form-${actualSectionId}-sellingPlanOptions`);
    const sellingPlanInput = actualForm?.querySelector(`#product-form-${actualSectionId}-sellingPlan`);
    this.updateSellingPlanSelection(packSize, sellingPlanOptions, sellingPlanInput);

    const subLabelParagraph = actualForm?.querySelector('label.purchaseType-subscription > div > p');
    const customRadioLabels = actualForm?.querySelectorAll('.custom-radio label');

    if (subLabelParagraph) {
      if (packSize === 1) {
        subLabelParagraph.innerHTML = `Subscribe &amp;&nbsp;<span class="purchaseType-subscription__savings">Save <span class="subscription-savings">${this.initialSubscriptionSavingsPercentage}</span></span>`;
        customRadioLabels.forEach(label => label.classList.remove('is-packs'));
      } else {
        subLabelParagraph.innerHTML = 'Subscribe &amp; Save';
        customRadioLabels.forEach(label => label.classList.add('is-packs'));
      }
    }
    
    this.updateSubscriptionSavings(packSize);

    const subscriptionRadioBtn = actualForm?.querySelector(`input#product-form-${actualSectionId}-purchaseType--subscription`);
    const isSubActive = subscriptionRadioBtn && subscriptionRadioBtn.checked;
    this.updatePackSaveTexts(isSubActive);

    const quantityInput = actualForm?.querySelector('input[name="quantity"]');

    if (packSize === 1) {
      if (variantsWrapper) variantsWrapper.style.display = 'block';
      if (customVariants) customVariants.style.display = 'none';
      if (quantityInput) quantityInput.value = 1;

      actualForm?.querySelectorAll('.custom-variant-item input[type="number"]').forEach(input => {
        input.value = 0;
      });

      // Reset 1-Pack Prices
      const selectedVariantData = this.getSelectedVariantData();
      

      const subLabelStrike = actualForm?.querySelector('label.purchaseType-subscription .label__strike-price');
      const subLabelFinal = actualForm?.querySelector('label.purchaseType-subscription .label__final-price');
      const otLabelStrike = actualForm?.querySelector('label.purchaseType-one-time .label__strike-price');
      const otLabelFinal = actualForm?.querySelector('label.purchaseType-one-time .label__final-price');

      if (selectedVariantData) {
        const { price, subPrice, serving, servingSubscription, element } = selectedVariantData;
        
        if (subLabelStrike) subLabelStrike.textContent = price;
        if (subLabelFinal) subLabelFinal.textContent = subPrice;
        if (otLabelFinal) otLabelFinal.textContent = price;
        if (otLabelStrike) {
          otLabelStrike.textContent = price; 
        }
        
        const isSubscriptionCurrently = actualForm?.querySelector(`input#product-form-${actualSectionId}-purchaseType--subscription`)?.checked;
      }
    } else {
      // Multi-pack mode
      if (variantsWrapper) {
        variantsWrapper.style.display = 'none';
      }
      if (customVariants) {
        customVariants.style.display = 'block';
      }
      // Don't set quantity to pack size - it should be calculated from variant selections
      // The total quantity will be updated when user selects variant quantities

      this.updateMultiPackPrices();
    }

    this.hideError();
    this.updateQuantityButtonStates();
  }

  setupEventListeners(eventOptions) {
    // Form submission handler
    const submitButton = this.productForm.querySelector('.product__form-submit');
    if (submitButton) {
      submitButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleFormSubmission(e);
      }, eventOptions);
    }

    // Purchase type selection handlers - use working alternative selector
    const purchaseLabels = this.productForm.querySelectorAll('.purchaseType');
    
    if (purchaseLabels.length > 0) {
      purchaseLabels.forEach((label, index) => {
        label.addEventListener('click', (e) => {
          const labelParent = label.closest('.custom-radio');
          const isSubscription = label.classList.contains('purchaseType-subscription');

          // Remove active from all custom-radio elements
          this.productForm.querySelectorAll('.custom-radio').forEach(radio => {
            radio.classList.remove('custom-radio--active');
            const input = radio.querySelector('input');
            if (input) input.checked = false;
          });

          // Add active to clicked option
          if (labelParent) {
            labelParent.classList.add('custom-radio--active');
            const input = labelParent.querySelector('input');
            if (input) {
              input.checked = true;
            }
          }

          this.handlePurchaseTypeSelection(isSubscription);
        }, eventOptions);
      });
    } else {
      console.warn('No purchase type labels found with .purchaseType class!');
    }

    // Variant selector button click handler (to toggle dropdown)
    if (this.elements.productVariantsSelectBtn) {
      this.elements.productVariantsSelectBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const variantSelect = e.target.closest('.product__variants-select');
        if (variantSelect) {
          variantSelect.classList.toggle('toggle');
        }
      }, eventOptions);
    }

    // Variant selection handlers (includes flavor selector navigation)
    if (this.elements.productVariantSelectOption && this.elements.productVariantSelectOption.length > 0) {
      this.elements.productVariantSelectOption.forEach((option) => {
        option.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleVariantSelection(e.target.closest('.product__variants-select-dropdown-item'));
        }, eventOptions);
      });
    }

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      const variantSelect = e.target.closest('.product__variants-select');
      if (!variantSelect) {
        document.querySelectorAll('.product__variants-select').forEach(select => {
          select.classList.remove('toggle');
        });
      }
    }, eventOptions);

    // Variant selector radio button handlers (fallback for ProductHero integration)
    const variantSelectorRadios = this.productForm.querySelectorAll('.variant-selector__radio');
    if (variantSelectorRadios.length > 0) {
      variantSelectorRadios.forEach((radio) => {
        radio.addEventListener('change', (e) => {
          if (e.target.checked) {
            // Update visual state
            this.updateVariantSelectorState(e.target);
            // Update pricing using existing variant change handler
            this.handleVariantChange(e.target);
          }
        }, eventOptions);
      });
    }

    // Handle visual variant selector clicks (for regular variants, not flavor navigation)
    const variantGridItems = this.elements.getVariantGridItems();
    if (variantGridItems.length > 0) {
      variantGridItems.forEach(item => {
        item.addEventListener('click', (e) => {
          const variantId = item.getAttribute('data-variant-id');
          const variantType = item.getAttribute('data-type');
          const variantPrice = item.getAttribute('data-price');
          const variantSubPrice = item.getAttribute('data-sub-price');
          const variantServingText = item.getAttribute('data-serving');
          const variantServingSubscriptionText = item.getAttribute('data-serving-subscription');
          
          // Update selected variant
          if (this.elements.productId && variantId) {
            this.elements.productId.value = variantId;
          }

          // Update selected state
          variantGridItems.forEach(gridItem => {
            gridItem.classList.remove('selected');
          });
          item.classList.add('selected');

          // Update price display
          this.handleVariantChange({
            value: variantId,
            dataset: {
              subId: item.getAttribute('data-sub-id'),
              price: variantPrice,
              subPrice: variantSubPrice,
              subDiscount: item.getAttribute('data-sub-discount'),
              serving: variantServingText,
              servingSubscription: variantServingSubscriptionText
            }
          });

          // Update URL to reflect selected variant
          this.updateUrlWithVariant(variantId);

          // Find and display any variant-specific images
          if (this.elements.carouselSlides) {
            this.elements.carouselSlides.forEach((slide, index) => {
              const slideVariantIds = slide.dataset.variant;
              if (slideVariantIds) {
                const variantIdsArray = slideVariantIds.split(',').map(id => id.trim());
                if (variantIdsArray.includes(variantId)) {
                  this.goToSlide(index);
                }
              }
            });
          }

          // Update callouts for the selected variant
          this.showCalloutsForVariant(variantId);
        }, eventOptions);
      });
    }

    // Prevent selling plan dropdown from triggering label clicks
    if (this.elements.sellingPlanOptions) {
      this.elements.sellingPlanOptions.addEventListener('click', (e) => {
        e.stopPropagation();
      }, eventOptions);
      
      this.elements.sellingPlanOptions.addEventListener('change', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const selectedValue = e.target.value;
        
        // Mark that user has made a selection
        e.target.dataset.userSelected = 'true';
        
        // Update hidden input
        if (this.elements.sellingPlan) {
          this.elements.sellingPlan.value = selectedValue;
        }
        
        // Find selling plan input by alternative selectors if main element not found
        const sellingPlanInputAlt = this.productForm.querySelector('input[id*="sellingPlan"]');
        if (sellingPlanInputAlt) {
          sellingPlanInputAlt.value = selectedValue;
        }
        
        
      }, eventOptions);
    }

    // Also set up event handlers for alternative selling plan selectors
    const sellingPlanOptionsAlt = this.productForm.querySelector('#product-form-' + this.sectionId + '-sellingPlanOptions');
    
    if (sellingPlanOptionsAlt && sellingPlanOptionsAlt !== this.elements.sellingPlanOptions) {
      sellingPlanOptionsAlt.addEventListener('change', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const selectedValue = e.target.value;
        
        // Mark that user has made a selection
        e.target.dataset.userSelected = 'true';
        
        // Update any hidden selling plan inputs
        const sellingPlanInputs = this.productForm.querySelectorAll('input[id*="sellingPlan"]');
        sellingPlanInputs.forEach(input => {
          input.value = selectedValue;
        });
        
        
      }, eventOptions);
    }
  }

  // Update visual state of variant selector
  updateVariantSelectorState(selectedRadio) {
    // Remove active class from all variant labels
    const variantLabels = this.productForm.querySelectorAll('.variant-option-label');
    variantLabels.forEach(label => {
      label.classList.remove('active');
    });

    // Add active class to selected variant label
    const selectedLabel = selectedRadio.closest('.variant-option-label');
    if (selectedLabel) {
      selectedLabel.classList.add('active');
    }
  }

  // Handle variant changes for regular variants (not flavor navigation)
  handleVariantChange(variant) {
    const variantId = variant.value || variant.dataset.id;
    const variantSubscriptionId = variant.dataset.subId;
    const variantPrice = variant.dataset.price;
    const variantSubscriptionPrice = variant.dataset.subPrice;
    const variantSubDiscount = variant.dataset.subDiscount;

    // Safely update elements only if they exist
    if (this.elements.onetimePrice) {
      this.elements.onetimePrice.innerHTML = variantPrice;
    }
    if (this.elements.onetimeOptionPrice) {
      this.elements.onetimeOptionPrice.innerHTML = variantPrice;
    }

    // Only update subscription pricing if subscription plan exists
    if (variantSubscriptionPrice && variantSubscriptionPrice !== '0') {
      if (this.elements.subscriptionOptionPrice) {
        this.elements.subscriptionOptionPrice.innerHTML = variantSubscriptionPrice;
      }
      if (this.elements.subscriptionOptionPriceDel) {
        this.elements.subscriptionOptionPriceDel.innerHTML = variantPrice;
      }
    }

    // Update the price in the label elements
    if (this.elements.subLabelStrike) this.elements.subLabelStrike.innerHTML = variantPrice;
    if (this.elements.subLabelFinal) this.elements.subLabelFinal.innerHTML = variantSubscriptionPrice;
    if (this.elements.otLabelFinal) this.elements.otLabelFinal.innerHTML = variantPrice;



    // Update form variant ID
    if (this.elements.productId && variantId) {
      this.elements.productId.value = variantId;
    }

    // Update URL
    this.updateUrlWithVariant(variantId);

    // Navigate to variant image
    if (this.elements.carouselSlides) {
      this.elements.carouselSlides.forEach((slide, index) => {
        const slideVariantIds = slide.dataset.variant;
        if (slideVariantIds) {
          const variantIdsArray = slideVariantIds.split(',').map(id => id.trim());
          if (variantIdsArray.includes(variantId)) {
            this.goToSlide(index);
          }
        }
      });
    }
  }

  // Initialize savings percentage from DOM
  initializeSavingsPercentage() {
    const savingsSpan = this.productForm.querySelector('.subscription-savings');
    if (savingsSpan) {
      this.initialSubscriptionSavingsPercentage = savingsSpan.textContent.trim();
    } else {
      this.initialSubscriptionSavingsPercentage = '15%'; // Default fallback
    }
  }

  updateMultiPackPrices() {
    if (!this.productForm) return;

    const activePackButton = this.elements.getActivePackButton();
    if (!activePackButton) return;

    const packSize = parseInt(activePackButton.getAttribute('data-pack-size'));
    if (packSize === 1) return; // No multi-pack pricing for 1-pack

    const customVariantItems = this.elements.getCustomVariantItems();
    let totalPrice = 0;
    let totalSubPrice = 0;
    let hasValidSelection = false;

    customVariantItems.forEach(item => {
      const input = item.querySelector('input[type="number"]');
      const quantity = parseInt(input.value || 0);
      if (quantity > 0) {
        hasValidSelection = true;
        const variantPrice = parseFloat(item.dataset.variantPrice || 0);
        const planPercentageDecimal = parseFloat(this.initialSubscriptionSavingsPercentage?.replace('%', '') || 0) / 100;
        const variantSubPrice = variantPrice * (1 - planPercentageDecimal);
        
        totalPrice += variantPrice * quantity;
        totalSubPrice += variantSubPrice * quantity;
      }
    });

    if (!hasValidSelection) return;

    // Update label prices only since main price display is in purchase selector
    const formatPrice = (cents) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(cents / 100);
    };

    // Update label prices
    if (this.elements.subLabelStrike) this.elements.subLabelStrike.textContent = formatPrice(totalPrice);
    if (this.elements.subLabelFinal) this.elements.subLabelFinal.textContent = formatPrice(totalSubPrice);
    if (this.elements.otLabelFinal) this.elements.otLabelFinal.textContent = formatPrice(totalPrice);
  }

  updatePackSaveTexts(isSubscription) {
    if (!this.productForm) return;

    const packButtons = this.elements.getPackOptionButtons();
    
    packButtons.forEach(button => {
      const packSize = parseInt(button.getAttribute('data-pack-size'));
      const saveSpan = button.querySelector('.pack-save');
      if (!saveSpan) return;

      if (packSize === 1) {
        saveSpan.textContent = '';
        return;
      }

      // Get pack configuration for this size
      const packCfg = window.packConfig && window.packConfig[packSize];
      let savingsText = '';

      if (packCfg) {
        if (isSubscription && packCfg.savingsValueSub) {
          if (packCfg.savingsTypeSub === 'percentage') {
            savingsText = `Save ${packCfg.savingsValueSub}%`;
          } else if (packCfg.savingsTypeSub === 'fixed') {
            savingsText = `Save $${packCfg.savingsValueSub}`;
          }
        } else if (!isSubscription && packCfg.savingsValueOne) {
          if (packCfg.savingsTypeOne === 'percentage') {
            savingsText = `Save ${packCfg.savingsValueOne}%`;
          } else if (packCfg.savingsTypeOne === 'fixed') {
            savingsText = `Save $${packCfg.savingsValueOne}`;
          }
        }
      }

      // Fallback default savings text
      if (!savingsText) {
        if (packSize === 2) {
          savingsText = isSubscription ? 'Save 10%' : 'Save 5%';
        } else if (packSize === 3) {
          savingsText = isSubscription ? 'Save 15%' : 'Save 10%';
        }
      }

      saveSpan.textContent = savingsText;
    });
  }

  updateSubscriptionSavings(packSize) {
    if (!this.productForm) return;

    const subscriptionInfo = this.elements.subscriptionInfo;
    if (!subscriptionInfo) return;

    const benefitsList = subscriptionInfo.querySelector('ul');
    if (!benefitsList) return;

    // Update the savings text in subscription benefits
    // const savingsListItem = benefitsList.querySelector('li:first-child');
    // if (savingsListItem) {
    //   if (packSize === 1) {
    //     // For 1-pack, show the original subscription savings percentage
    //     const savingsPercentage = this.initialSubscriptionSavingsPercentage || '15%';
    //     savingsListItem.innerHTML = `Save <span>${savingsPercentage}</span> on every order`;
    //   } else {
    //     // For multi-packs, show generic savings text
    //     savingsListItem.innerHTML = 'Additional savings on bulk orders';
    //   }
    // }
  }

  getTotalSelectedQuantity() {
    let total = 0;
    const customVariantItems = this.elements.getCustomVariantItems();
    
    customVariantItems.forEach(item => {
      const input = item.querySelector('input[type="number"]');
      if (input) {
        total += parseInt(input.value || 0, 10);
      }
    });
    return total;
  }

  showError(message) {
    const errorContainer = document.querySelector('.product__form-checkout .form-error');
    if (!errorContainer) {
      console.error('Error container not found');
      return;
    }

    const errorMessage = errorContainer.querySelector('.error-message');
    if (!errorMessage) {
      console.error('Error message element not found');
      return;
    }

    errorMessage.textContent = message;
    errorContainer.classList.add('active');
    errorContainer.style.display = 'block';

    setTimeout(() => {
      errorContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  }

  hideError() {
    const errorContainer = document.querySelector('.product__form-checkout .form-error');
    if (!errorContainer) return;

    errorContainer.classList.remove('active');

    setTimeout(() => {
      if (!errorContainer.classList.contains('active')) {
        errorContainer.style.display = 'none';
      }
    }, 300);
  }

  setupQuantityButtons() {
    // Set up quantity button event listeners for custom variants
    // Use event delegation to avoid duplicate handlers
    const customVariantsInForm = this.productForm.querySelector('.product__custom-variants');
    const customVariantsGlobal = document.querySelector('.product__custom-variants');
    const customVariants = customVariantsInForm || customVariantsGlobal;
    
    if (!customVariants) return;

    // Remove any existing listeners first
    const existingHandler = customVariants.getAttribute('data-qty-handler');
    if (existingHandler) {
      return;
    }

    // Mark that we've set up handlers
    customVariants.setAttribute('data-qty-handler', 'true');
    
    // Use event delegation on the container
    customVariants.addEventListener('click', (e) => {
      const button = e.target.closest('.qty-btn');
      if (!button) return;
      
      e.preventDefault();
      e.stopPropagation();
      
      const isPlus = button.classList.contains('plus');
      const variantItem = button.closest('.custom-variant-item');
      const input = variantItem.querySelector('input[type="number"]');
      const currentValue = parseInt(input.value) || 0;
      const maxValue = parseInt(input.getAttribute('max')) || 99;
      const variantInventory = parseInt(variantItem.dataset.inventoryQuantity) || 0;
      const variantPolicy = variantItem.dataset.inventoryPolicy;
      
      if (isPlus) {
        // Check inventory limits
        if (variantPolicy === 'deny' && currentValue >= variantInventory) {
          return;
        }
        if (currentValue < maxValue) {
          input.value = currentValue + 1;
        }
      } else {
        // Minus button
        if (currentValue > 0) {
          input.value = currentValue - 1;
        }
      }
      
      // Update button states, total quantity, and multi-pack prices
      this.updateQuantityButtonStates();
      this.updateTotalQuantity();
      this.updateMultiPackPrices();
      this.validatePackRequirements();
    });
  }



  updateQuantityButtonStates() {
    // Update the state of quantity buttons based on current values
    const customVariantsInForm = this.productForm.querySelector('.product__custom-variants');
    const customVariantsGlobal = document.querySelector('.product__custom-variants');
    const customVariants = customVariantsInForm || customVariantsGlobal;
    
    if (!customVariants) return;

    const variantItems = customVariants.querySelectorAll('.custom-variant-item');
    variantItems.forEach(variantItem => {
      const input = variantItem.querySelector('input[type="number"]');
      const minusBtn = variantItem.querySelector('.qty-btn.minus');
      const plusBtn = variantItem.querySelector('.qty-btn.plus');
      
      if (!input || !minusBtn || !plusBtn) return;
      
      const currentValue = parseInt(input.value) || 0;
      const maxValue = parseInt(input.getAttribute('max')) || 99;
      const variantInventory = parseInt(variantItem.dataset.inventoryQuantity) || 0;
      const variantPolicy = variantItem.dataset.inventoryPolicy;
      
      // Disable minus button if at 0
      minusBtn.disabled = currentValue <= 0;
      
      // Disable plus button if at max or no inventory
      let disablePlus = currentValue >= maxValue;
      if (variantPolicy === 'deny' && currentValue >= variantInventory) {
        disablePlus = true;
      }
      plusBtn.disabled = disablePlus;
    });
  }

  updateTotalQuantity() {
    // Calculate total quantity from all variant selections and update hidden input
    const customVariantsInForm = this.productForm.querySelector('.product__custom-variants');
    const customVariantsGlobal = document.querySelector('.product__custom-variants');
    const customVariants = customVariantsInForm || customVariantsGlobal;
    
    if (!customVariants) return;

    let totalQuantity = 0;
    const variantInputs = customVariants.querySelectorAll('.custom-variant-item input[type="number"]');
    variantInputs.forEach(input => {
      totalQuantity += parseInt(input.value) || 0;
    });

    // Update the hidden quantity input - find in form containing the custom variants
    const parentForm = customVariants.closest('form');
    const quantityInput = parentForm?.querySelector('input[name="quantity"]') || this.productForm.querySelector('input[name="quantity"]');
    
    if (quantityInput) {
      quantityInput.value = totalQuantity;
    }
  }

  validatePackRequirements() {
    // Check if total selected quantity meets the pack size requirement
    const customVariantsInForm = this.productForm.querySelector('.product__custom-variants');
    const customVariantsGlobal = document.querySelector('.product__custom-variants');
    const customVariants = customVariantsInForm || customVariantsGlobal;
    
    if (!customVariants) return true; // No custom variants, validation passes

    // Get current pack size from active button
    const activePackButton = document.querySelector('.pack-option-btn.active');
    const requiredPackSize = activePackButton ? parseInt(activePackButton.getAttribute('data-pack-size')) || 1 : 1;
    
    // Skip validation for 1-pack
    if (requiredPackSize === 1) return true;

    // Calculate total selected quantity
    let totalQuantity = 0;
    const variantInputs = customVariants.querySelectorAll('.custom-variant-item input[type="number"]');
    variantInputs.forEach(input => {
      totalQuantity += parseInt(input.value) || 0;
    });

    // Find error container
    const parentForm = customVariants.closest('form');
    let errorContainer = parentForm?.querySelector('.form-error') || document.querySelector('.form-error');
    
    if (!errorContainer) {
      // Create error container if it doesn't exist
      errorContainer = document.createElement('div');
      errorContainer.className = 'form-error';
      errorContainer.style.display = 'none';
      const formCheckout = parentForm?.querySelector('.product__form-checkout') || document.querySelector('.product__form-checkout');
      if (formCheckout) {
        formCheckout.appendChild(errorContainer);
      }
    }

    if (totalQuantity < requiredPackSize) {
      const remaining = requiredPackSize - totalQuantity;
      errorContainer.textContent = `Please select ${remaining} more item${remaining > 1 ? 's' : ''} to complete your ${requiredPackSize}-pack.`;
      errorContainer.style.display = 'block';
      errorContainer.classList.add('active');
      return false; // Validation failed
    } else if (totalQuantity > requiredPackSize) {
      const excess = totalQuantity - requiredPackSize;
      errorContainer.textContent = `You have selected ${excess} too many items for a ${requiredPackSize}-pack. Please adjust your selection.`;
      errorContainer.style.display = 'block';
      errorContainer.classList.add('active');
      return false; // Validation failed
    } else {
      // Exactly the right amount
      errorContainer.style.display = 'none';
      errorContainer.classList.remove('active');
      return true; // Validation passed
    }
  }

  setupFormValidation() {
    // Set up form submission validation to prevent add to cart with invalid pack selection
    const forms = document.querySelectorAll('form[data-type="add-to-cart-form"]');
    
    forms.forEach(form => {
      // Remove any existing listeners first
      if (form.hasAttribute('data-validation-setup')) {
        return;
      }
      form.setAttribute('data-validation-setup', 'true');
      
      form.addEventListener('submit', (e) => {
        // Run pack validation
        const isValid = this.validatePackRequirements();
        
        if (!isValid) {
          e.preventDefault();
          e.stopPropagation();
          
          // Scroll to error message
          const errorContainer = document.querySelector('.form-error.active');
          if (errorContainer) {
            errorContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          
          return false;
        }
      });
    });
  }

  handleFormSubmission(e) {
    e.preventDefault();
    e.stopPropagation();

    const form = e.target.closest('form');
    if (!form) return;

    // Run pack validation
    const isValid = this.validatePackRequirements();
    
    if (!isValid) {
      // Scroll to error message
      const errorContainer = document.querySelector('.form-error.active');
      if (errorContainer) {
        errorContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return false;
    }

    // Check if this is a multi-pack submission requiring special handling
    const activePackButton = this.elements.getActivePackButton();
    const packSize = activePackButton ? parseInt(activePackButton.getAttribute('data-pack-size')) : 1;
    
    if (packSize > 1) {
      // Handle multi-pack cart submission
      this.handleMultiPackSubmission(form);
    } else {
      // Handle regular single product submission
      this.handleSingleProductSubmission(form);
    }
  }

  handlePurchaseTypeSelection(isSubscription) {
    if (!this.productForm) return;

    const activePackButton = this.elements.getActivePackButton();
    const packSize = activePackButton ? parseInt(activePackButton.getAttribute('data-pack-size')) : 1;

    // Find the selling plan input by ID (which doesn't change when we remove name attribute)
    const sellingPlanInput = document.querySelector('input[id*="sellingPlan"]');
    const sellingPlanOptionsEl = this.elements.sellingPlanOptions || this.elements.sellingPlanOptionsAlt;
    
    // Find subscription info element
    const subscriptionInfo = this.elements.subscriptionInfo || this.elements.subscriptionInfoAlt || this.productForm.querySelector('.option__subscription-info');

    if (isSubscription) {
      if (subscriptionInfo) {
        subscriptionInfo.classList.add('active');
      }
      if (sellingPlanInput) {
        sellingPlanInput.setAttribute('name', 'selling_plan');
      }
      
      if (sellingPlanOptionsEl && sellingPlanInput) {
        // Only update selling plan selection if no user selection has been made
        if (!sellingPlanOptionsEl.dataset.userSelected) {
          this.updateSellingPlanSelection(packSize, sellingPlanOptionsEl, sellingPlanInput);
        }
      }

    } else { // One-time purchase
      if (subscriptionInfo) {
        subscriptionInfo.classList.remove('active');
      }
      if (sellingPlanInput) {
        sellingPlanInput.removeAttribute('name'); // Remove name for one-time
      }
    }
    
    this.updatePackSaveTexts(isSubscription); 
    this.updateSubscriptionSavings(packSize); 

    if (packSize > 1) { // Multi-pack
      this.updateMultiPackPrices(); // Recalculate and display multi-pack prices based on new purchase type
    }
  }

  handleVariantSelection(selectedOption) {
    if (!selectedOption) {
      console.warn('No option selected');
      return;
    }

    // Check if this is a flavor selector (product navigation) or variant selector
    const productUrl = selectedOption.dataset.productUrl;
    
    if (productUrl) {
      // This is a flavor product navigation
      window.location.href = productUrl;
      return;
    }
    
    // Original variant selection logic for regular variants
    if (!selectedOption.classList.contains('product__variants-select-dropdown-item--selected')) {
      const variantSelect = selectedOption.closest('.product__variants-select');
      const parent = variantSelect?.querySelector('.product__variants-select-btn');
      const selectedOptionType = selectedOption.getAttribute('data-type');
      const selectedOptionID = selectedOption.getAttribute('data-id');
      const selectedOptionSubID = selectedOption.getAttribute('data-sub-id');
      const selectedOptionPrice = selectedOption.getAttribute('data-price');
      const selectedOptionSubPrice = selectedOption.getAttribute('data-sub-price');

      // Remove selected class from all options
      if (this.elements.productVariantSelectOption) {
        this.elements.productVariantSelectOption.forEach(opt => {
          opt.classList.remove('product__variants-select-dropdown-item--selected');
        });
      }
      
      // Add selected class to clicked option
      selectedOption.classList.add('product__variants-select-dropdown-item--selected');
      
      // Close dropdown by removing toggle class
      if (variantSelect) {
        variantSelect.classList.remove('toggle');
      }
      
      // Update the button text and data
      if (parent && selectedOptionType) {
        parent.setAttribute('data-type', selectedOptionType);
        const titleElement = selectedOption.querySelector('.product__variants-select-dropdown-item-title');
        if (titleElement) {
          parent.innerHTML = titleElement.innerHTML;
        }
      }
      
      // Update variant ID in form
      if (selectedOptionID && this.elements.productId) {
        this.elements.productId.value = selectedOptionID;
      }
      


      // Update URL with variant
      this.updateUrlWithVariant(selectedOptionID);
      
      // Navigate to variant image if available
      if (this.elements.carouselSlides && selectedOptionID) {
        this.elements.carouselSlides.forEach((slide, index) => {
          const slideVariantId = slide.dataset.variant;
          if (slideVariantId && selectedOptionID == slideVariantId) {
            this.goToSlide(index);
          }
        });
      }


      
      // Update callouts for the selected variant
      this.showCalloutsForVariant(selectedOptionID);

      // Close the dropdown after selection by removing toggle class
      if (variantSelect) {
        variantSelect.classList.remove('toggle');
      }
    }
  }

  toggleVariantDropdown() {
    const variantSelect = this.productForm.querySelector('.product__variants-select');
    if (variantSelect) {
      variantSelect.classList.toggle('toggle');
    }
  }

  initializeCarouselAndVariants() {
    // Initialize carousel navigation if available
    if (window.emblaInstance) {
      const defaultVariantId = this.productForm.querySelector('input[name="id"]')?.value;
      if (defaultVariantId) {
        setTimeout(() => {
          this.showCalloutsForVariant(defaultVariantId);
        }, 300);
      }
    }
  }

  // Helper function to update URL with variant
  updateUrlWithVariant(variantId) {
    if (!variantId) return;
    
    const url = new URL(window.location);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url);
  }

  // Helper function to navigate carousel
  goToSlide(index) {
    if (typeof window.emblaInstance !== 'undefined' && typeof window.emblaInstance.scrollTo === 'function') {
      window.emblaInstance.scrollTo(index);
    }
  }



  // Helper function to show callouts for variant
  showCalloutsForVariant(variantId) {
    if (window.emblaGoToVariant) {
      window.emblaGoToVariant(variantId);
      return;
    }
    
    // Fallback for desktop - find the slide and show callouts
    const variantSlides = document.querySelectorAll(`.embla__slide[data-variant*="${variantId}"]`);
    if (variantSlides.length === 0) {
      return;
    }
    
    const targetSlide = variantSlides[0];
    const slides = document.querySelectorAll('.embla__slide');
    let targetIndex = -1;
    
    for (let i = 0; i < slides.length; i++) {
      if (slides[i] === targetSlide) {
        targetIndex = i;
        break;
      }
    }
    
    if (targetIndex !== -1) {
      this.showCalloutsForSlide(targetIndex);
    }
  }

  // Helper function to show callouts for specific slide
  showCalloutsForSlide(slideIndex) {
    const slides = document.querySelectorAll('.embla__slide');
    if (!slides || !slides[slideIndex]) {
      return;
    }
    
    const currentSlide = slides[slideIndex];
    const hasCallouts = !!currentSlide.querySelector('.product__callouts-container');
    
    // Hide all callouts first
    slides.forEach(slide => {
      const callouts = slide.querySelectorAll('.product__callouts-container');
      callouts.forEach(callout => {
        callout.style.display = 'none';
      });
    });
    
    // Show callouts for the current slide
    if (hasCallouts) {
      const callouts = currentSlide.querySelectorAll('.product__callouts-container');
      callouts.forEach(callout => {
        callout.style.display = 'flex';
      });
    }
  }

  // Handle multi-pack form submission
  handleMultiPackSubmission(form) {
    const button = form.querySelector('.product__form-submit');
    const originalText = button?.innerHTML || 'Add to Cart';
    
    // Collect all selected variants and quantities
    const items = [];
    const customVariantItems = this.elements.getCustomVariantItems();
    
    customVariantItems.forEach(item => {
      const input = item.querySelector('input[type="number"]');
      const quantity = parseInt(input.value || 0);
      if (quantity > 0) {
        const variantId = item.dataset.variantId;
        if (variantId) {
          items.push({
            id: parseInt(variantId),
            quantity: quantity
          });
        }
      }
    });

    if (items.length === 0) {
      this.showError('Please select at least one product variant.');
      return;
    }

    this.addItemsToCart(items, button, originalText);
  }

  // Handle single product form submission
  handleSingleProductSubmission(form) {
    // For single products, use standard form submission
    const formData = new FormData(form);
    const button = form.querySelector('.product__form-submit');
    const originalText = button?.innerHTML || 'Add to Cart';
    
    if (button) {
      button.innerHTML = '<span>Adding to cart...</span>';
      button.classList.add('btn--loading');
      button.disabled = true;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      body: formData
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json();
    })
    .then(data => {
      // Success - refresh cart and open if available
      if (window.Boost && window.Boost.Cart) {
        window.Boost.Cart.refresh()
          .then(() => {
            if (button) {
              button.innerHTML = originalText;
              button.classList.remove('btn--loading');
              button.disabled = false;
            }
            window.Boost.Cart.open();
          })
          .catch(err => {
            console.error('Error refreshing Boost cart:', err);
            if (button) {
              button.innerHTML = originalText;
              button.classList.remove('btn--loading');
              button.disabled = false;
            }
            window.location.href = '/cart';
          });
      } else {
        // No Boost cart, redirect to regular cart
        if (button) {
          button.innerHTML = originalText;
          button.classList.remove('btn--loading');
          button.disabled = false;
        }
        window.location.href = '/cart';
      }
    })
    .catch(error => {
      console.error('Cart error:', error);
      if (button) {
        button.innerHTML = originalText;
        button.classList.remove('btn--loading');
        button.disabled = false;
      }
      this.showError('There was an error adding items to cart. Please try again.');
    });
  }

  // Function to add multiple items to cart
  addItemsToCart(items, button, originalText) {
    if (button) {
      button.innerHTML = '<span>Adding to cart...</span>';
      button.classList.add('btn--loading');
      button.disabled = true;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ items: items })
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json();
    })
    .then(data => {
      // After items are added, refresh and open cart if available
      if (window.Boost && window.Boost.Cart) {
        window.Boost.Cart.refresh()
          .then(() => {
            if (button) {
              button.innerHTML = originalText;
              button.classList.remove('btn--loading');
              button.disabled = false;
            }
            window.Boost.Cart.open();
          })
          .catch(err => {
            console.error('Error refreshing Boost cart:', err);
            if (button) {
              button.innerHTML = originalText;
              button.classList.remove('btn--loading');
              button.disabled = false;
            }
            window.location.href = '/cart';
          });
      } else {
        // No Boost cart, redirect to regular cart
        if (button) {
          button.innerHTML = originalText;
          button.classList.remove('btn--loading');
          button.disabled = false;
        }
        window.location.href = '/cart';
      }
    })
    .catch(error => {
      console.error('Cart error:', error);
      if (button) {
        button.innerHTML = originalText;
        button.classList.remove('btn--loading');
        button.disabled = false;
      }
      this.showError(error.description || 'There was an error adding items to cart. Please try again.');
    });
  }

  cleanupMainEventListeners() {
    if (this.mainEventController) {
      this.mainEventController.abort();
      this.mainEventController = null;
    }
  }

  cleanupQuantityButtons() {
    if (this.quantityButtonCleanup) {
      this.quantityButtonCleanup();
      this.quantityButtonCleanup = null;
    }
  }

  cleanup() {
    this.cleanupMainEventListeners();
    this.cleanupQuantityButtons();
    if (window.variantCalloutsObserver) {
      window.variantCalloutsObserver.disconnect();
      window.variantCalloutsObserver = null;
    }
  }
}

// Global functions that need to remain accessible
window.packClickHandler = function(buttonElement, packSizeStr) {
  // Check if pack functionality is available
  if (!buttonElement) {
    console.warn('Pack click handler called without button element');
    return;
  }

  // Find the appropriate ProductHero instance and call its method
  const formElement = buttonElement.closest('form');
  
  // Use getAttribute to avoid name collision with input[name="id"]
  const formId = formElement?.getAttribute('id');
  
  if (formElement && formId && typeof formId === 'string') {
    const sectionId = formId.replace('product-form-', '');
    const availableKeys = Object.keys(window.productHeroInstances || {});
    
    // Try exact match first
    if (window.productHeroInstances && window.productHeroInstances[sectionId]) {
      window.productHeroInstances[sectionId].packClickHandler(buttonElement, packSizeStr);
      return;
    }
    
    // Find any instance that belongs to the same base section (they should all work for the same product)
    const matchingKey = availableKeys.find(key => {
      const keyBase = key.split('__')[0]; // Get template--19777851359469
      const sectionBase = sectionId.split('__')[0]; // Get template--19777851359469
      return keyBase === sectionBase;
    });
    
    if (matchingKey && window.productHeroInstances[matchingKey]) {
      window.productHeroInstances[matchingKey].packClickHandler(buttonElement, packSizeStr);
    } else if (availableKeys.length > 0 && window.productHeroInstances[availableKeys[0]]) {
      window.productHeroInstances[availableKeys[0]].packClickHandler(buttonElement, packSizeStr);
    } else {
      console.warn('No ProductHero instance found for pack click handler');
    }
  } else {
    console.warn('Could not find form element for pack button');
  }
};

// Variant Callouts Functionality
// Thumbnail carousel functionality - following official Embla pattern
function addThumbBtnsClickHandlers(emblaApiMain, emblaApiThumb) {
  const slidesThumbs = emblaApiThumb.slideNodes()

  const scrollToIndex = slidesThumbs.map(
    (_, index) => () => {
      emblaApiMain.scrollTo(index)
      
      // Trigger callouts on mobile for responsive feedback
      const isMobile = window.innerWidth <= 768
      if (isMobile && typeof showCalloutsForSlide === 'function') {
        setTimeout(() => {
          showCalloutsForSlide(index)
        }, 100)
      }
    }
  )

  slidesThumbs.forEach((slideNode, index) => {
    slideNode.addEventListener('click', scrollToIndex[index], false)
  })

  return () => {
    slidesThumbs.forEach((slideNode, index) => {
      slideNode.removeEventListener('click', scrollToIndex[index], false)
    })
  }
}

function addToggleThumbBtnsActive(emblaApiMain, emblaApiThumb) {
  const slidesThumbs = emblaApiThumb.slideNodes()

  const toggleThumbBtnsState = () => {
    emblaApiThumb.scrollTo(emblaApiMain.selectedScrollSnap())
    const previous = emblaApiMain.previousScrollSnap()
    const selected = emblaApiMain.selectedScrollSnap()
    
    if (slidesThumbs[previous]) {
      slidesThumbs[previous].classList.remove('embla-thumbs__slide--selected')
    }
    if (slidesThumbs[selected]) {
      slidesThumbs[selected].classList.add('embla-thumbs__slide--selected')
    }
    
    // Ensure callouts are shown on mobile
    const isMobile = window.innerWidth <= 768
    if (isMobile && typeof showCalloutsForSlide === 'function') {
      showCalloutsForSlide(selected)
    }
  }

  emblaApiMain.on('select', toggleThumbBtnsState)
  emblaApiThumb.on('init', toggleThumbBtnsState)

  return () => {
    const selected = emblaApiMain.selectedScrollSnap()
    if (slidesThumbs[selected]) {
      slidesThumbs[selected].classList.remove('embla-thumbs__slide--selected')
    }
  }
}

function setupThumbnails(emblaMain) {
  const thumbsViewport = document.querySelector('.embla-thumbs__viewport')
  if (!thumbsViewport) return null

  // Initialize thumbnail carousel following official pattern
  const emblaThumb = EmblaCarousel(thumbsViewport, {
    containScroll: 'keepSnaps',
    dragFree: true
  })

  // Set up handlers following official pattern
  const removeThumbBtnsClickHandlers = addThumbBtnsClickHandlers(emblaMain, emblaThumb)
  const removeToggleThumbBtnsActive = addToggleThumbBtnsActive(emblaMain, emblaThumb)

  // Clean up on destroy
  emblaMain
    .on('destroy', removeThumbBtnsClickHandlers)
    .on('destroy', removeToggleThumbBtnsActive)

  emblaThumb
    .on('destroy', removeThumbBtnsClickHandlers)
    .on('destroy', removeToggleThumbBtnsActive)

  return emblaThumb
}

// Legacy pagination dots functionality (for fallback)
function setupPagination(embla) {
  const dotsContainer = document.querySelector('.embla__dots')
  if (!dotsContainer) return

  // Clear existing dots first
  dotsContainer.innerHTML = ''

  const slides = embla.slideNodes()
  const dots = slides.map((_, index) => {
    const dot = document.createElement('button')
    dot.className = 'embla__dot'
    dot.setAttribute('aria-label', `Go to slide ${index + 1}`)
    dot.addEventListener('click', () => embla.scrollTo(index))
    return dot
  })
  
  dotsContainer.append(...dots)
  
  const updateDots = () => {
    dots.forEach((dot, index) => {
      dot.classList.toggle('embla__dot--selected', index === embla.selectedScrollSnap())
    })
  }
  
  embla.on('select', updateDots)
  updateDots() // Initial state
}

// Variant navigation for Embla
function setupVariantNavigation(embla) {
  window.emblaGoToVariant = function(variantId) {
    const slides = document.querySelectorAll(`.embla__slide[data-variant*="${variantId}"]`)
    if (slides.length === 0) return

    const targetSlide = slides[0]
    const allSlides = document.querySelectorAll('.embla__slide')
    
    for (let i = 0; i < allSlides.length; i++) {
      if (allSlides[i] === targetSlide) {
        embla.scrollTo(i)
        break
      }
    }
  }
}

document.addEventListener('DOMContentLoaded', function() {
  
  // Initialize Embla Carousel (responsive with CSS control)
  function initEmblaCarousel() {
    const emblaNode = document.querySelector('.embla__viewport')
    if (emblaNode && typeof EmblaCarousel !== 'undefined') {
      
      try {
        const embla = EmblaCarousel(emblaNode, {
          align: 'start',
          dragFree: false,
          loop: false,
          containScroll: 'trimSnaps',
          skipSnaps: false,
          watchDrag: (emblaApi) => {
            // Only allow dragging on mobile (let CSS handle the visual layout)
            return window.innerWidth <= 768
          }
        })
        
        // Setup thumbnails on mobile (CSS will handle visibility)
        const emblaThumb = setupThumbnails(embla)
        
        // Store both instances globally
        window.emblaThumbInstance = emblaThumb
        
        // Setup variant navigation
        setupVariantNavigation(embla)
        
        // Handle slide changes
        embla.on('select', () => {
          const isMobile = window.innerWidth <= 768
          if (isMobile) {
            showCalloutsForSlide(embla.selectedScrollSnap())
          }
        })
        
        // Initial callouts display
        embla.on('init', () => {
          const isMobile = window.innerWidth <= 768
          if (isMobile) {
            showCalloutsForSlide(embla.selectedScrollSnap())
          } else {
            showCalloutsForSlide(0) // Show first slide callouts on desktop
          }
        })
        
        // Store global reference for variant navigation
        window.emblaInstance = embla
        
        return embla
      } catch (error) {
        console.error('Embla: Failed to initialize:', error)
        return null
      }
    }
    return null
  }

  // Handle responsive behavior without destroy/reinit
  let resizeTimeout
  function handleResize() {
    clearTimeout(resizeTimeout)
    resizeTimeout = setTimeout(() => {
      const isMobile = window.innerWidth <= 768
      
      if (window.emblaInstance) {
        // Update drag behavior based on screen size
        window.emblaInstance.reInit({
          watchDrag: () => isMobile
        })
        
        // Reset to first slide and show appropriate callouts
        window.emblaInstance.scrollTo(0, false) // false = no animation
        showCalloutsForSlide(0)
      }
    }, 150)
  }
  
  // Initialize carousel with delay for DOM readiness
  setTimeout(() => {
    initEmblaCarousel()
  }, 500)
  
  // Handle window resize
  window.addEventListener('resize', handleResize)
  
  // Initialize with the default variant
  const defaultVariantId = document.querySelector('input[name="id"]')?.value
  if (defaultVariantId) {
    setTimeout(() => {
      showCalloutsForVariant(defaultVariantId)
    }, 300)
  }
});

function showCalloutsForVariant(variantId) {
  // Use Embla variant navigation if available (mobile)
  if (window.emblaGoToVariant) {
    window.emblaGoToVariant(variantId)
    return
  }
  
  // Fallback for desktop - find the slide and show callouts
  const variantSlides = document.querySelectorAll(`.embla__slide[data-variant*="${variantId}"]`)
  if (variantSlides.length === 0) {
    return
  }
  
  const targetSlide = variantSlides[0]
  const slides = document.querySelectorAll('.embla__slide')
  let targetIndex = -1
  
  for (let i = 0; i < slides.length; i++) {
    if (slides[i] === targetSlide) {
      targetIndex = i
      break
    }
  }
  
  if (targetIndex !== -1) {
    showCalloutsForSlide(targetIndex)
  }
}

function showCalloutsForSlide(slideIndex) {
  const slides = document.querySelectorAll('.embla__slide')
  if (!slides || !slides[slideIndex]) {
    return
  }
  
  const currentSlide = slides[slideIndex]
  const hasCallouts = !!currentSlide.querySelector('.product__callouts-container')
  
  // Hide all callouts first
  slides.forEach(slide => {
    const callouts = slide.querySelectorAll('.product__callouts-container')
    callouts.forEach(callout => {
      callout.style.display = 'none'
    })
  })
  
  // Show callouts for the current slide
  if (hasCallouts) {
    const callouts = currentSlide.querySelectorAll('.product__callouts-container')
    callouts.forEach(callout => {
      callout.style.display = 'flex'
    })
  }
}

// Selling plan dropdown functionality - simplified and working
document.addEventListener('DOMContentLoaded', () => {
  // Find all selling plan selects on the page
  const sellingPlanSelects = document.querySelectorAll('select[id*="sellingPlanOptions"]');
  
  sellingPlanSelects.forEach((select) => {
    select.addEventListener('change', (e) => {
      const selectedValue = e.target.value;
      
      // Find corresponding hidden input
      const hiddenInput = select.closest('form').querySelector('input[id*="sellingPlan"]');
      
      if (hiddenInput) {
        hiddenInput.value = selectedValue;
        
        // Check if subscription is selected and add name attribute
        const subscriptionRadio = select.closest('form').querySelector('input[id*="purchaseType--subscription"]');
        if (subscriptionRadio && subscriptionRadio.checked) {
          hiddenInput.setAttribute('name', 'selling_plan');
        }
        
        console.log('Selling plan updated to:', selectedValue);
      }
    });
  });
  
  // Handle purchase type changes - both visual state and name attribute
  const purchaseTypeLabels = document.querySelectorAll('.purchaseType');
  purchaseTypeLabels.forEach((label) => {
    label.addEventListener('click', (e) => {
      const labelParent = label.closest('.custom-radio');
      const form = label.closest('form');
      const isSubscription = label.classList.contains('purchaseType-subscription');
      
      // Remove active from all custom-radio elements in this form
      form.querySelectorAll('.custom-radio').forEach(radio => {
        radio.classList.remove('custom-radio--active');
        const input = radio.querySelector('input');
        if (input) input.checked = false;
      });

      // Add active to clicked option
      if (labelParent) {
        labelParent.classList.add('custom-radio--active');
        const input = labelParent.querySelector('input');
        if (input) {
          input.checked = true;
        }
      }

      // Handle selling plan name attribute
      const hiddenInput = form.querySelector('input[id*="sellingPlan"]');
      if (hiddenInput) {
        if (isSubscription) {
          hiddenInput.setAttribute('name', 'selling_plan');
        } else {
          hiddenInput.removeAttribute('name');
        }
      }

      // Handle subscription info visibility
      const subscriptionInfo = form.querySelector('.option__subscription-info');
      if (subscriptionInfo) {
        if (isSubscription) {
          subscriptionInfo.classList.add('active');
        } else {
          subscriptionInfo.classList.remove('active');
        }
      }
      
      console.log('Purchase type changed to:', isSubscription ? 'subscription' : 'one-time');
    });
  });

  // Handle flavor selector dropdown functionality
  const flavorSelectors = document.querySelectorAll('.product__variants-select');
  flavorSelectors.forEach((selector) => {
    const button = selector.querySelector('.product__variants-select-btn');
    const dropdown = selector.querySelector('.product__variants-select-dropdown');
    const options = selector.querySelectorAll('.product__variants-select-dropdown-item');

    if (button && dropdown && options.length > 0) {
      // Handle button click to toggle dropdown
      button.addEventListener('click', (e) => {
        e.preventDefault();
        selector.classList.toggle('toggle');
      });

      // Handle option selection
      options.forEach((option) => {
        option.addEventListener('click', (e) => {
          e.preventDefault();
          
          // Check if this is a flavor navigation (has product URL)
          const productUrl = option.dataset.productUrl;
          
          if (productUrl) {
            // Navigate to the flavor product
            window.location.href = productUrl;
            return;
          }

          // Handle regular variant selection (if needed)
          // Remove selected class from all options
          options.forEach(opt => {
            opt.classList.remove('product__variants-select-dropdown-item--selected');
          });
          
          // Add selected class to clicked option
          option.classList.add('product__variants-select-dropdown-item--selected');
          
          // Update button content
          const optionTitle = option.querySelector('.product__variants-select-dropdown-item-title');
          if (optionTitle) {
            button.innerHTML = optionTitle.innerHTML;
          }
          
          // Close dropdown
          selector.classList.remove('toggle');
        });
      });
    }
  });

  // Close dropdown when clicking outside
  document.addEventListener('click', (e) => {
    const variantSelect = e.target.closest('.product__variants-select');
    if (!variantSelect) {
      document.querySelectorAll('.product__variants-select').forEach(select => {
        select.classList.remove('toggle');
      });
    }
  });
});

// Initialize ProductHero instances
window.productHeroInstances = {};

window.addEventListener('load', (e) => {
  // Find all actual product forms (not just elements with IDs starting with product-form-)
  const forms = document.querySelectorAll('form[id^="product-form-"]');
  
  forms.forEach(form => {
    if (form.id && typeof form.id === 'string') {
      const sectionId = form.id.replace('product-form-', '');
      
      window.productHeroInstances[sectionId] = new ProductHero(sectionId);
    }
  });
});